import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pollingQueryOptions } from '@/lib/query-polling';
import { useDataSources } from '@/services/datasources';
import { useFaixasCanal } from '@/hooks/useFaixasCanal';
import { asInstrumentId } from '@/domain';
import {
  nivelDeInstrumento, nivelDeInstrumentoMultiCanal, piorNivel, semSinalVisivel,
  temFaixaDeControle,
  type NivelExibido,
} from '@/domain/instrumentos/nivel-exibido';
import type { FaixaCanal } from '@/domain/instrumentos/tilt';
import { canalDaSerie, leituraAtualDaSerie, temGraficoDeSerie } from '../domain/chart-data';
import type { Canal, Instrument, Reading } from '@/domain';
import { foraDeServico } from '@/domain/instrumentos/fora-de-servico';

/**
 * A leitura do canal MAIS GRAVE entre os que têm faixa — o número que acompanha o
 * chip de nível no cabeçalho. Mostrar a leitura de outro canal ao lado de um chip
 * "ALERTA" faria o RT ler o valor errado como sendo o que disparou o alerta.
 */
function piorLeituraComFaixa(
  leituras: readonly Reading[],
  canaisComFaixa: readonly Canal[],
): Reading | undefined {
  const comFaixa = leituras.filter((r) => r.canal !== null && canaisComFaixa.includes(r.canal));
  if (comFaixa.length === 0) return undefined;
  const pior = piorNivel(comFaixa.map((r) => r.level ?? 'NORMAL'));
  return comFaixa.find((r) => (r.level ?? 'NORMAL') === pior);
}

export interface M6GraficoData {
  instruments: Instrument[];
  /** Nível atual (leitura mais recente) por instrumento. */
  levelById: Record<string, NivelExibido>;
  /** Leitura mais recente por instrumento. */
  currentById: Record<string, Reading | undefined>;
  /** Série do selecionado. Para tiltímetro é o EIXO A (`seriesEixoB` traz o outro). */
  series: Reading[];
  /** GUPM-270 — 2ª série do tiltímetro. Vazia para instrumento de canal único. */
  seriesEixoB: Reading[];
  /** GUPM-270 — faixa vigente do eixo A do selecionado (régua do gráfico). `null` se não houver. */
  faixaCanal: FaixaCanal | null;
  /** Série da régua do reservatório (instrumento RL da mesma barragem), se houver. */
  reguaSeries: Reading[];
  /** Série de pluviometria (instrumento EM/PLV da mesma barragem), se houver. */
  pluviSeries: Reading[];
  isLoading: boolean;
  /** Só a série do selecionado carregando (ex: troca de instrumento) — sem bloquear a tela toda. */
  isLoadingSeries: boolean;
  error: Error | null;
}

/** Dados do gráfico M6: instrumentos + níveis atuais + série do selecionado + overlays. */
export function useInstrumentSeries(selectedId: string | null): M6GraficoData {
  const { instrumentacao } = useDataSources();

  const { data: instruments = [], isLoading: loadingInst, error: errInst } = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentacao.listInstruments(),
  });

  // GUPM-228 — nível/valor atual vêm da última leitura POR INSTRUMENTO
  // (`readings_current`), não do top-100 GLOBAL. O feed global é dominado pelos
  // tiltímetros (leem de 30 em 30 min): com 1,6 M leituras ele cobre poucas
  // horas, então a maioria dos instrumentos não aparecia nele e o M6 exibia
  // "Valor atual: —" com nível NORMAL verde — para instrumento com milhares de
  // leituras. É o mesmo conserto que o GUPM-136 fez no Dashboard e no Mapa; o
  // M6 tinha ficado de fora.
  const { data: latest = [], isLoading: loadingLatest, error: errLatest } = useQuery({
    queryKey: ['readings', 'current'],
    queryFn: () => instrumentacao.listCurrentReadings(),
    ...pollingQueryOptions,
  });

  // GUPM-270 — régua do tiltímetro por eixo. Hook compartilhado com o Dashboard e
  // o Mapa (mesma queryKey ⇒ uma requisição por sessão, não três).
  const { faixasCanal, error: errFaixas } = useFaixasCanal();

  const selectedInstEarly = instruments.find((i) => i.id === selectedId) ?? null;
  const ehTilt = selectedInstEarly?.type === 'TILT';
  const canalPrincipal = selectedInstEarly ? canalDaSerie(selectedInstEarly.type) : null;

  // GUPM-270 — instrumento multi-canal precisa de UMA busca POR CANAL. Sem o
  // filtro, `limit: 60` traz os três canais embaralhados: 20 pontos por série numa
  // curva só, com a temperatura (dezenas de °C) esmagando a escala dos eixos.
  const { data: series = [], isLoading: loadingSeries, error: errSeries } = useQuery({
    queryKey: ['readings', 'series', selectedId, canalPrincipal ?? 'unico'],
    queryFn: () => instrumentacao.listLatestReadings({
      instrumentId: asInstrumentId(selectedId!),
      limit: 60,
      ...(canalPrincipal ? { canal: canalPrincipal } : {}),
    }),
    // Marco não desenha gráfico (GUPM-329) — a série não é buscada; o histórico
    // completo vem de `useHistoricoInstrumento`.
    enabled: !!selectedId && !!selectedInstEarly && temGraficoDeSerie(selectedInstEarly.type),
  });

  const { data: seriesEixoB = [], error: errEixoB } = useQuery({
    queryKey: ['readings', 'series', selectedId, 'eixo_b'],
    queryFn: () => instrumentacao.listLatestReadings({
      instrumentId: asInstrumentId(selectedId!), limit: 60, canal: 'eixo_b' as const,
    }),
    enabled: !!selectedId && ehTilt,
  });

  // Overlays do gráfico (fiel ao slide): régua do reservatório e pluviometria são
  // instrumentos próprios (RL e EM/PLV) da MESMA barragem do selecionado. Existem
  // só quando a carga tiver esses instrumentos → o overlay some com elegância.
  const selectedInst = instruments.find((i) => i.id === selectedId) ?? null;
  const damId = selectedInst?.damId ?? null;
  const reguaInst = damId ? instruments.find((i) => i.damId === damId && i.type === 'RL') : undefined;
  const pluviInst = damId
    ? instruments.find((i) => i.damId === damId && (i.type === 'EM' || i.type === 'PLV'))
    : undefined;

  const { data: reguaSeries = [], error: errRegua } = useQuery({
    queryKey: ['readings', 'series', 'regua', reguaInst?.id ?? null],
    queryFn: () => instrumentacao.listLatestReadings({ instrumentId: reguaInst!.id, limit: 60 }),
    enabled: !!reguaInst,
  });

  const { data: pluviSeries = [], error: errPluvi } = useQuery({
    queryKey: ['readings', 'series', 'pluvi', pluviInst?.id ?? null],
    queryFn: () => instrumentacao.listLatestReadings({ instrumentId: pluviInst!.id, limit: 60 }),
    enabled: !!pluviInst,
  });

  // Overlays são best-effort: uma falha NÃO derruba o gráfico (não entra no `error`
  // bloqueante), mas não pode ser 100% silenciosa — sem log, "falhou" e "a barragem
  // não tem esse instrumento" ficam indistinguíveis (crítico quando a API real chegar).
  useEffect(() => {
    if (errRegua) console.warn('[M6] overlay régua indisponível', { damId, reguaId: reguaInst?.id, err: errRegua });
  }, [errRegua, damId, reguaInst?.id]);
  useEffect(() => {
    if (errPluvi) console.warn('[M6] overlay pluviometria indisponível', { damId, pluviId: pluviInst?.id, err: errPluvi });
  }, [errPluvi, damId, pluviInst?.id]);

  const levelById: Record<string, NivelExibido> = {};
  const currentById: Record<string, Reading | undefined> = {};
  for (const inst of instruments) {
    const doInstrumento = latest.filter((x) => x.instrumentId === inst.id);
    const canaisComFaixa = inst.canaisComFaixa ?? [];

    if (canaisComFaixa.length > 0) {
      // GUPM-270 — multi-canal: o nível é o PIOR canal com faixa. Antes o hook
      // ordenava por `ts` e pegava o primeiro; com três canais no mesmo timestamp
      // isso era sorteio, e a linha da temperatura podia virar o nível exibido.
      levelById[inst.id] = nivelDeInstrumentoMultiCanal(
        doInstrumento,
        canaisComFaixa,
        // GUPM-303 — o chip e o cabecalho do grafico sao a pagina do instrumento.
        semSinalVisivel(inst),
        // GUPM-307 — e a pagina do instrumento e onde o RT vai olhar o historico de um
        // instrumento retirado: o chip nao pode afirmar nivel a partir da leitura velha.
        foraDeServico(inst),
      );
      // "Valor atual" do cabeçalho: o canal do PIOR nível, para o número casar com
      // o chip ao lado dele.
      currentById[inst.id] = piorLeituraComFaixa(doInstrumento, canaisComFaixa);
    } else {
      // GUPM-329 — marco: a leitura do canal que o gráfico segue (a cota).
      const r = leituraAtualDaSerie(doInstrumento, inst.type);
      // GUPM-228 -- sem leitura nao entra como NORMAL (chip e cabecalho ficam neutros).
      levelById[inst.id] = nivelDeInstrumento(
        r, temFaixaDeControle(inst), semSinalVisivel(inst), foraDeServico(inst),
      );
      currentById[inst.id] = r;
    }
  }

  return {
    instruments,
    levelById,
    currentById,
    series,
    seriesEixoB,
    // Régua do gráfico: a faixa do eixo A. Os dois eixos compartilham a escala e,
    // hoje, a mesma banda — a divergência entre eixos é tratada na tabela, onde as
    // colunas de limiar somem em vez de mostrar a régua de um eixo ao lado do outro.
    faixaCanal: faixasCanal.find(
      (f) => f.instrumentId === selectedId && f.canal === 'eixo_a',
    ) ?? null,
    reguaSeries,
    pluviSeries,
    // Carga inicial da tela (instrumentos + níveis). NÃO inclui a série, pra que
    // trocar de instrumento não derrube a tela toda — isso fica em isLoadingSeries.
    isLoading: loadingInst || loadingLatest,
    isLoadingSeries: !!selectedId && loadingSeries,
    // `errFaixas`/`errEixoB` entram no erro bloqueante: sem faixa o gráfico do
    // tiltímetro sai sem régua, e sem o eixo B ele mostra metade do instrumento —
    // os dois são o conteúdo da tela, não overlay best-effort.
    error: errInst ?? errLatest ?? errSeries ?? errFaixas ?? errEixoB,
  };
}
